import type React from 'react';
import type { CSSProperties } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useWordsHappenTheme } from '../../theme';
import styles from './mode-card.module.scss';

export interface ModeCardProps {
  color: string;
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  onClick: () => void;
  className?: string;
}

// ---------------------------------------------------------------------------
// WCAG relative luminance helpers
// ---------------------------------------------------------------------------

function parseCssColor(value: string): [number, number, number] | null {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 1;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.fillStyle = value;
  ctx.fillRect(0, 0, 1, 1);
  const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
  return [r / 255, g / 255, b / 255];
}

function linearise(c: number): number {
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

function relativeLuminance(r: number, g: number, b: number): number {
  return 0.2126 * linearise(r) + 0.7152 * linearise(g) + 0.0722 * linearise(b);
}

function contrastRatio(l1: number, l2: number): number {
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

const WHITE_LUMINANCE = 1.0;

function resolveColor(el: HTMLElement | null, token: string): string | null {
  if (!el || typeof getComputedStyle === 'undefined') return null;
  const raw = token.trim();
  if (raw.startsWith('var(')) {
    const propName = raw.slice(4, -1).trim();
    return getComputedStyle(el).getPropertyValue(propName).trim() || null;
  }
  return raw;
}

function useForegroundColor(colorToken: string, sentinelRef: React.RefObject<HTMLElement | null>): string {
  const [fg, setFg] = useState<string>('white');

  useEffect(() => {
    function compute() {
      const resolved = resolveColor(sentinelRef.current, colorToken);
      if (!resolved) return;

      const rgb = parseCssColor(resolved);
      if (!rgb) return;

      const bgLum = relativeLuminance(...rgb);
      const contrastWithWhite = contrastRatio(WHITE_LUMINANCE, bgLum);
      // --wh-on-sun is dark in every theme, where --wh-ink is light on Noir.
      setFg(contrastWithWhite >= 4.5 ? 'white' : 'var(--wh-on-sun)');
    }

    compute();

    const observer = new MutationObserver(compute);
    const root = document.documentElement;
    observer.observe(root, { attributes: true, subtree: true, attributeFilter: ['data-wh-theme'] });
    return () => observer.disconnect();
  }, [colorToken, sentinelRef]);

  return fg;
}

// ---------------------------------------------------------------------------
// ModeCard
// ---------------------------------------------------------------------------

export const ModeCard: React.FC<ModeCardProps> = ({ color, title, subtitle, icon, onClick, className }) => {
  const { theme } = useWordsHappenTheme();
  const [pressed, setPressed] = useState(false);
  const sentinelRef = useRef<HTMLButtonElement | null>(null);

  const isMeadow = theme === 'meadow';
  const isPressed = isMeadow && pressed;

  const fg = useForegroundColor(color, sentinelRef);

  const cls = [styles.card, isMeadow ? styles.meadow : styles.flat, isPressed ? styles.pressed : null, className]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      ref={sentinelRef}
      type="button"
      className={cls}
      onClick={onClick}
      style={{ '--card-bg': color, '--card-color': fg } as CSSProperties}
      onPointerDown={() => setPressed(true)}
      onPointerUp={() => setPressed(false)}
      onPointerLeave={() => setPressed(false)}
    >
      <span className={styles.icon} aria-hidden="true">
        {icon}
      </span>
      <span className={styles.title}>{title}</span>
      {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
    </button>
  );
};
